import React from "react";
import "./CDTable.scss";
import PropTypes from "prop-types";
import classNames from "classnames";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import { uuidv7 as uuid } from "uuidv7";
import NoResults from "../NoResults/NoResults";

export function CDTable(props) {
  const {
    columns,
    className,
    children,
    showNoResults,
    autoLayout,
    customNoResults,
    headStyle,
  } = props;

  return (
    <>
      <table
        className={classNames("cdt-table", className, {
          "cdt-table--layout-auto": showNoResults || autoLayout,
        })}
      >
        <CDTableHead columns={columns} style={headStyle} />
        {!showNoResults && children}
      </table>

      {showNoResults && (
        <div className="table-no-result">
          {customNoResults || <NoResults />}
        </div>
      )}
    </>
  );
}

CDTable.propTypes = {
  showNoResults: PropTypes.bool,
  autoLayout: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      defaultMessage: PropTypes.string,
      toLeft: PropTypes.bool,
      minWidth: PropTypes.number,
      tooltip: PropTypes.shape({
        id: PropTypes.string,
        defaultMessage: PropTypes.string,
        alignRight: PropTypes.bool,
      }),
    }),
  ).isRequired,
  children: PropTypes.node.isRequired,
};

export function CDTableHead({ columns = [], style }) {
  return (
    <thead style={style}>
      <tr>
        {columns.map((column) => {
          if (!column) return null;
          return (
            <th
              key={column.id}
              className={column.className}
              width={column?.width}
              style={{
                ...(column?.minWidth ? { minWidth: column.minWidth } : null),
                ...(column?.sticky
                  ? { position: "sticky", left: column.stickyLeft }
                  : null),
              }}
              data-sticky-td={column?.sticky || undefined}
              data-sticky-last-left-td={column?.stickyLast || undefined}
            >
              <div
                className={classNames(
                  "cdt-table__header-column",
                  `cdt-table__header--column-${column.id}`,
                  {
                    "cdt-table__header-column--left": column.toLeft,
                  },
                )}
              >
                {!column.empty && (
                  <>
                    {column.beforeTextComponent}
                    {column.defaultMessage && (
                      <div className="cdt-table__header-column__text">
                        <IDHFormattedMessage
                          id={column.id}
                          defaultMessage={column.defaultMessage}
                        />
                      </div>
                    )}
                    {column.afterTextComponent}
                    {column.tooltip && (
                      <MaterialTooltip
                        center
                        className={column.tooltip.className}
                        clickable={column.tooltip.clickable}
                        alignRight={column.tooltip.alignRight}
                        content={
                          <IDHFormattedMessage
                            id={column.tooltip.id}
                            defaultMessage={column.tooltip.defaultMessage}
                          />
                        }
                      />
                    )}
                  </>
                )}
                {column.selectAll && (
                  <CustomCheckbox
                    id={`${column.id}-${uuid()}`}
                    name={column.id}
                    label={
                      <IDHFormattedMessage
                        id={column.id}
                        defaultMessage={column.defaultMessage}
                      />
                    }
                    {...column.selectAll}
                  />
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export function CDTableBody({ children }) {
  return <tbody>{children}</tbody>;
}

export function CDTableRow({ children, className, highlighted, ...props }) {
  return (
    <tr
      className={classNames(
        "cdt-table__row",
        {
          "cdt-table__row--highlighted": highlighted,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function CDTableColumn({ children, className, ...props }) {
  return (
    <td className={classNames(["cdt-table__cell", className])} {...props}>
      {children}
    </td>
  );
}
